import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { matchesSearch } from '../../../utils/searchUtils';
import './Select.css';

export type SelectOption = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
};

export type SelectProps = {
  id?: string;
  /** Native form submit */
  name?: string;
  className?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** Dropdown list max height (px) */
  listMaxHeight?: number;
  /** Extra class on the portaled listbox (e.g. topbar-specific menu styles) */
  dropdownClassName?: string;
  /** Dropdown ichida qidiruv maydoni */
  searchable?: boolean;
  searchPlaceholder?: string;
  emptySearchMessage?: string;
  /** Qidiruv filtri (default: label bo‘yicha matchesSearch) */
  filterOption?: (option: SelectOption, query: string) => boolean;
};

const DEFAULT_LIST_MAX = 280;

function readSelectDropdownZ(): number {
  if (typeof document === 'undefined') return 10050;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--ui-select-dropdown-z').trim();
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 10050;
}

/** Tooltip uchun — faqat oddiy matnli label */
function plainLabelForTitle(label: React.ReactNode): string | undefined {
  if (typeof label === 'string' || typeof label === 'number') {
    return String(label);
  }
  return undefined;
}

function nextEnabledIndex(options: SelectOption[], from: number, direction: 1 | -1): number {
  const n = options.length;
  if (n === 0) return -1;
  let i = from;
  for (let step = 0; step < n; step += 1) {
    i = (i + direction + n) % n;
    if (!options[i]?.disabled) return i;
  }
  return -1;
}

export function Select({
  id: idProp,
  name,
  className = '',
  value,
  onChange,
  options,
  placeholder = '',
  disabled = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  listMaxHeight = DEFAULT_LIST_MAX,
  dropdownClassName,
  searchable = false,
  searchPlaceholder = '',
  emptySearchMessage = '',
  filterOption,
}: SelectProps) {
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const triggerId = idProp ?? `${reactId}-trigger`;
  const searchInputId = `${reactId}-search`;

  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setHighlighted(-1);
    setSearchQuery('');
  }, []);

  const visibleOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const q = searchQuery.trim();
    return options.filter((opt) => {
      if (opt.value === '' || opt.value == null) return false;
      if (filterOption) return filterOption(opt, q);
      const label = plainLabelForTitle(opt.label) ?? '';
      return matchesSearch(label, q);
    });
  }, [options, searchable, searchQuery, filterOption]);

  const listOptions = searchable && searchQuery.trim() ? visibleOptions : options;

  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: listMaxHeight,
    zIndex: 10050,
  });

  const matchedOption = useMemo(() => {
    const v = value == null ? '' : String(value);
    return options.find((o) => String(o.value) === v);
  }, [options, value]);

  /** Tanlanmagan / noto‘g‘ri qiymat — kulrang uslub */
  const isUnset = useMemo(() => {
    if (value === '' || value == null) return true;
    if (!matchedOption) return true;
    return matchedOption.value === '' || String(matchedOption.value).trim() === '';
  }, [value, matchedOption]);

  /** Ko‘rinadigan matn: avvalo `placeholder`, bo‘lmasa bo‘sh qiymatli opsiya labeli, keyin topilgan opsiya */
  const displayValue = useMemo((): React.ReactNode => {
    if (matchedOption && matchedOption.value !== '' && String(matchedOption.value).trim() !== '') {
      return matchedOption.label;
    }
    if (placeholder) return placeholder;
    if (matchedOption) return matchedOption.label;
    const emptyOpt = options.find((o) => o.value === '' || o.value == null);
    return emptyOpt?.label ?? '';
  }, [matchedOption, placeholder, options]);

  const showPlaceholder = isUnset;

  /** Joylashuv + kontent bo‘yicha avtomatik kenglik (viewport chegarasi bilan) */
  const syncMenuLayout = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const gap = 4;
    const maxH = listMaxHeight;
    let top = r.bottom + gap;
    let maxHeight = maxH;
    const spaceBelow = window.innerHeight - r.bottom - gap - 8;
    const spaceAbove = r.top - gap - 8;
    if (spaceBelow < 120 && spaceAbove > spaceBelow) {
      const h = Math.min(maxH, Math.max(80, spaceAbove));
      top = Math.max(8, r.top - gap - h);
      maxHeight = h;
    } else {
      maxHeight = Math.min(maxH, Math.max(80, spaceBelow));
    }

    const horizontalPad = 16;
    const vwRoom = Math.max(120, window.innerWidth - r.left - horizontalPad);
    const absMax = Math.min(560, vwRoom);

    let widthPx = r.width;
    const list = listRef.current;
    if (open && list) {
      list.classList.add('ui-select__dropdown--measuring');
      void list.offsetHeight;
      widthPx = Math.max(r.width, list.scrollWidth);
      list.classList.remove('ui-select__dropdown--measuring');
      widthPx = Math.min(Math.max(widthPx, r.width), absMax);
    } else {
      widthPx = Math.min(Math.max(widthPx, r.width), absMax);
    }

    setMenuStyle({
      position: 'fixed',
      top,
      left: r.left,
      width: widthPx,
      maxWidth: widthPx,
      maxHeight,
      zIndex: readSelectDropdownZ(),
    });
  }, [listMaxHeight, open, listOptions.length]);

  useLayoutEffect(() => {
    if (!open || !searchable) return;
    const id = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, searchable]);

  useLayoutEffect(() => {
    if (!open) return;
    const run = () => syncMenuLayout();
    run();
    const id = requestAnimationFrame(() => run());
    window.addEventListener('scroll', run, true);
    window.addEventListener('resize', run);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('scroll', run, true);
      window.removeEventListener('resize', run);
    };
  }, [open, syncMenuLayout, listOptions]);

  useEffect(() => {
    if (!open) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      closeDropdown();
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
  }, [open, closeDropdown]);

  useEffect(() => {
    if (!open || highlighted < 0) return;
    const node = listRef.current?.querySelector(`[data-opt-index="${highlighted}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, open]);

  useLayoutEffect(() => {
    if (!open) {
      setHighlighted(-1);
      return;
    }
    const opts = listOptions;
    const sel = opts.findIndex((o) => String(o.value) === String(value) && !o.disabled);
    setHighlighted(sel >= 0 ? sel : nextEnabledIndex(opts, -1, 1));
  }, [open, value, listOptions]);

  const selectIndex = (idx: number) => {
    const opt = listOptions[idx];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    closeDropdown();
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeDropdown();
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => nextEnabledIndex(listOptions, h < 0 ? -1 : h, 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => nextEnabledIndex(listOptions, h < 0 ? 0 : h, -1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0) selectIndex(highlighted);
      return;
    }
    if (e.key === 'Tab') {
      closeDropdown();
    }
  };

  const onKeyDownTrigger = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'Tab') {
      closeDropdown();
      return;
    }
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        closeDropdown();
      }
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (highlighted >= 0) selectIndex(highlighted);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlighted(nextEnabledIndex(listOptions, -1, 1));
        return;
      }
      setHighlighted((h) => {
        const base = h < 0 ? listOptions.findIndex((o) => String(o.value) === String(value)) : h;
        return nextEnabledIndex(listOptions, base, 1);
      });
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlighted(nextEnabledIndex(listOptions, 0, -1));
        return;
      }
      setHighlighted((h) => {
        const base = h < 0 ? listOptions.findIndex((o) => String(o.value) === String(value)) : h;
        return nextEnabledIndex(listOptions, base, -1);
      });
    }
  };

  const pickOption = (idx: number, ev: React.PointerEvent | React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    selectIndex(idx);
  };

  const dropdown =
    open &&
    !disabled &&
    createPortal(
      <div
        ref={listRef}
        id={listboxId}
        role="listbox"
        className={['ui-select__dropdown', dropdownClassName].filter(Boolean).join(' ')}
        style={menuStyle}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >
        {searchable ? (
          <div
            className="ui-select__search-wrap"
            onPointerDown={(ev) => ev.stopPropagation()}
            onMouseDown={(ev) => ev.stopPropagation()}
          >
            <input
              ref={searchInputRef}
              id={searchInputId}
              type="search"
              className="ui-select__search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setHighlighted(-1);
              }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder || 'Search'}
              autoComplete="off"
              onKeyDown={onSearchKeyDown}
            />
          </div>
        ) : null}
        {listOptions.length === 0 && searchQuery.trim() ? (
          <div className="ui-select__empty" role="status">
            {emptySearchMessage || '—'}
          </div>
        ) : null}
        {listOptions.map((opt, idx) => {
          const selected = String(opt.value) === String(value);
          const hi = highlighted === idx;
          const titleHint = plainLabelForTitle(opt.label);
          return (
            <div
              key={`${String(opt.value)}-${idx}`}
              role="option"
              data-opt-index={idx}
              aria-selected={selected}
              title={titleHint}
              className={`ui-select__option${selected ? ' ui-select__option--selected' : ''}${
                hi ? ' ui-select__option--highlighted' : ''
              }${opt.disabled ? ' ui-select__option--disabled' : ''}`}
              onMouseEnter={() => !opt.disabled && setHighlighted(idx)}
              onPointerDown={(ev) => {
                if (opt.disabled || ev.button !== 0) return;
                pickOption(idx, ev);
              }}
            >
              {opt.label}
            </div>
          );
        })}
      </div>,
      document.body,
    );

  return (
    <div className={`ui-select ${className}`.trim()}>
      {name ? <input type="hidden" name={name} value={value} readOnly tabIndex={-1} aria-hidden /> : null}
      <button
        type="button"
        id={triggerId}
        ref={triggerRef}
        className={`ui-select__trigger${open ? ' ui-select__trigger--open' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabelledby ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledby}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
        }}
        onKeyDown={onKeyDownTrigger}
      >
        <span className={`ui-select__value${showPlaceholder ? ' ui-select__value--placeholder' : ''}`}>
          {displayValue}
        </span>
        <span className="ui-select__chevron" aria-hidden>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {dropdown}
    </div>
  );
}
